import { CommonModule, KeyValue } from '@angular/common'
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  numberAttribute,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { SvgIconComponent } from 'angular-svg-icon'
import { NzNotificationService } from 'ng-zorro-antd/notification'
import { NzSwitchComponent } from 'ng-zorro-antd/switch'
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip'
import { DevToolbarFeatureFlagService } from 'ngx-dev-toolbar'
import { MarkdownComponent } from 'ngx-markdown'
import {
  concat,
  map,
  Observable,
  Subscription,
  switchMap,
  tap,
  throwError,
} from 'rxjs'

import { ActiveSorting } from '../../abstracts/table-base.abstract'
import { AlertComponent } from '../../components/alert/alert.component'
import { BackButtonComponent } from '../../components/back-button/back-button.component'
import {
  ActiveSortingDefault,
  ActiveValidation,
} from '../../components/branch-detail-base/branch-detail-base.component'
import { ButtonComponent } from '../../components/button/button.component'
import { NumberComponent } from '../../components/number/number.component'
import { PageHeadlineComponent } from '../../components/page-headline/page-headline.component'
import { TypedTemplateDirective } from '../../directives/typed-template.directive'
import { ValidatedAddress } from '../../models/address.model'
import {
  AddressAttributeType,
  Attribute,
  AttributeConflict,
  ValidationStatus,
} from '../../models/attribute.model'
import { AddressRepository } from '../../repositories/address/address.repository'
import {
  AttributeRepository,
  SetAddressAttributesResponse,
} from '../../repositories/attribute/attribute.repository'
import { Expression } from '../../repositories/base.repository'
import { BranchService } from '../../services/branch.service'
import {
  SessionStoragePrefix,
  SyncWithSessionStorage,
} from '../../services/local-storage.service'

export type ViewMode = 'upload' | 'clearing'

export type TableDataCol = {
  value: number | string | null | undefined
  conflictId: string | null
}
export type TableDataCols = {
  [key: string]: TableDataCol | undefined
  MASTER_DATA_CONFLICT?: TableDataCol
  MOVEMENT_DATA_CONFLICT?: TableDataCol
  CSV?: TableDataCol
  DUPLICATE_IN_CSV?: TableDataCol
}

export type TableData = Attribute & {
  isResolved: boolean
  target: string | null | undefined
  cols: TableDataCols
}

@SessionStoragePrefix('conflictDetail')
@Component({
  selector: 'app-upload-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    TypedTemplateDirective,
    PageHeadlineComponent,
    BackButtonComponent,
    ButtonComponent,
    SvgIconComponent,
    NumberComponent,
    NzSwitchComponent,
    NzTooltipDirective,
    AlertComponent,
    MarkdownComponent,
  ],
  templateUrl: './conflict-detail.component.html',
})
export class ConflictDetailComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public readonly id!: string

  public viewMode: ViewMode = 'upload'

  private attributeSubscription!: Subscription
  private addressSubscription!: Subscription
  public isLoading = true

  public tableTemplateTypeToken!: {
    tableData: TableData[]
    label: string
    typeFilter: AddressAttributeType
  }

  public cellTemplateTypeToken!: {
    currentRow: TableData
    key: keyof TableDataCols | string
    conflict: TableDataCol | undefined
  }

  public address?: ValidatedAddress

  // table(s)
  public tableData: TableData[] = []
  public masterData: TableData[] = []
  public dynamicData: TableData[] = []

  // filter
  @SyncWithSessionStorage({ default: false })
  public showOnlyConflicts!: boolean

  // parent list params
  @SyncWithSessionStorage({ prefix: 'branchDetail', default: false })
  private readonly activeSorting: ActiveSorting = ActiveSortingDefault

  @SyncWithSessionStorage({ prefix: 'branchDetail', default: 'all' })
  private readonly activeValidation!: ActiveValidation

  @SyncWithSessionStorage({ prefix: 'branchDetail' })
  private readonly sword!: string

  @Input({ transform: numberAttribute })
  private readonly offset: number = 1

  @SyncWithSessionStorage({ prefix: 'branchDetail', default: false })
  private readonly parentShowOnlyConflicts: boolean = false

  // next button
  public nextId?: string = undefined
  public hasNextConflict: boolean = false

  public readonly autoResolveConflicts: ValidationStatus[] = [
    'NEW_ADDRESS',
    'MOVEMENT_DATA_CONFLICT',
  ]

  public allConflictCounts: Partial<Record<ValidationStatus, number>> = {}
  public addressConflictCounts: Partial<Record<ValidationStatus, number>> = {}
  public attributeConflictCounts: Partial<
    Record<Exclude<ValidationStatus, 'NEW_ADDRESS'>, number>
  > = {}
  public conflictTargetAddressIds: string[] = []

  public openAttributeConflicts?: number
  public openAddressConflicts?: number
  public unresolvedAttributeConflicts?: number
  public unresolvedAddressConflicts?: number

  public addressConflicts: AttributeConflict[] = []

  // view toggles
  public showPreviewOnly!: boolean

  public showDebug: boolean = false

  public constructor(
    private readonly attributeRepository: AttributeRepository,
    private readonly addressRepository: AddressRepository,
    private readonly route: ActivatedRoute,
    private readonly router: Router,

    private readonly notificationService: NzNotificationService,
    private readonly translateService: TranslateService,
    public readonly featureFlagsService: DevToolbarFeatureFlagService,
    private readonly elementRef: ElementRef,
    private readonly branchService: BranchService
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.id) return

    const branchId = this.route.snapshot.parent?.params.id

    if (branchId) {
      this.addressSubscription = this.addressRepository
        .getBranchAddresses(branchId, {
          limit: 1,
          page: this.offset + 1,
          filter: this.branchService.getFilter(
            this.sword,
            this.activeValidation,
            this.parentShowOnlyConflicts
          ),
          sorting: this.branchService.getSorting(this.activeSorting),
        })
        .pipe(
          map((res) => {
            this.hasNextConflict = res.data.length !== 0
            return res.data.at(0)?.id
          })
        )
        .subscribe((nextId) => (this.nextId = nextId))
    }

    this.updateTableData()
  }

  public ngOnInit(): void {
    this.viewMode = this.route.snapshot.data['viewMode']

    this.featureFlagsService.getForcedValues().subscribe((forcedFlags) => {
      this.showDebug = forcedFlags.some((flag) => flag.id === 'showDebug')
    })
  }

  public ngOnDestroy(): void {
    this.attributeSubscription?.unsubscribe()
    this.addressSubscription?.unsubscribe()
  }

  private updateTableData(): void {
    this.isLoading = true
    this.attributeSubscription = this.fetchTableData().subscribe()
  }

  private fetchTableData(): Observable<unknown> {
    const filter: Expression[] = []

    return concat(
      this.addressRepository.getById(this.id).pipe(
        tap((res) => {
          if (res)
            this.address = {
              ...res.data.attributes,
              id: res.data.id,
            }
        })
      ),

      this.attributeRepository
        .getAddressAttributes(this.id, {
          filter: filter,
        })
        .pipe(
          map((response) =>
            response.data.map(
              (item) =>
                ({
                  ...item.attributes,
                  id: item.id,
                }) as Attribute
            )
          ),
          map((attributes) => {
            // save and remove misleading global address conflicts from table data
            return attributes.map((a) => {
              if (a.key === 'referenceId') {
                this.addressConflicts = a.conflicts
                a.conflicts = a.conflicts.filter(
                  (c) => c.validationStatus !== 'MASTER_DATA_CONFLICT'
                )
              }
              return a
            })
          }),
          tap((attributes) => {
            this.conflictTargetAddressIds = attributes
              .flatMap((attribute) =>
                attribute.conflicts
                  .filter(
                    (conflict) =>
                      conflict.validationStatus === 'DUPLICATE_IN_CSV'
                  )
                  .map((conflict) => conflict.targetAddressId)
              )
              .filter((value, index, self) => self.indexOf(value) === index)
          }),
          tap(this.updateConflictCounts.bind(this)),
          tap(() => {
            this.showPreviewOnly =
              Object.keys(this.attributeConflictCounts).length === 0
          }),
          map((attributes) => {
            return attributes.map((attribute) => {
              const cols: TableDataCols = {}

              if (this.allConflictCounts['MOVEMENT_DATA_CONFLICT']) {
                cols.MASTER = attribute.conflicts
                  .filter(
                    (conflict) =>
                      conflict.validationStatus === 'MOVEMENT_DATA_CONFLICT'
                  )
                  .map((conflict) => ({
                    value: conflict.expectedValue,
                    conflictId: conflict.conflictId,
                  }))
                  .at(0)
              }

              if (this.allConflictCounts['MASTER_DATA_CONFLICT']) {
                cols.MASTER2 = attribute.conflicts
                  .filter(
                    (conflict) =>
                      conflict.validationStatus === 'MASTER_DATA_CONFLICT'
                  )
                  .map((conflict) => ({
                    value: conflict.expectedValue,
                    conflictId: conflict.conflictId,
                  }))
                  .at(0)
              }

              cols.CSV =
                attribute.conflicts.length === 0
                  ? undefined
                  : {
                      value: attribute.value,
                      conflictId: null,
                    }

              if (this.allConflictCounts['DUPLICATE_IN_CSV']) {
                for (
                  let i = 0, id = this.conflictTargetAddressIds[i];
                  i < this.conflictTargetAddressIds.length;
                  id = this.conflictTargetAddressIds[++i]
                ) {
                  cols['DUPLICATE_IN_CSV' + i] = attribute.conflicts
                    .filter(
                      (conflict) =>
                        conflict.validationStatus === 'DUPLICATE_IN_CSV' &&
                        conflict.targetAddressId === id
                    )
                    .map((conflict) => ({
                      value: conflict.expectedValue,
                      conflictId: conflict.conflictId,
                    }))
                    .at(0)
                }
              }

              // reapply resolved conflicts
              let target: string | null | undefined = undefined
              const targetConflict = attribute.conflicts.find(
                (conflict) => conflict.isResolution !== null
              )
              if (targetConflict?.isResolution === true) {
                target = targetConflict.conflictId
              }
              if (targetConflict?.isResolution === false) {
                target = null
              }

              return {
                ...attribute,
                conflicts: attribute.conflicts,
                cols: cols,

                isResolved: false,
                target: target,
              } satisfies TableData
            })
          }),
          tap((tableData) => (this.tableData = tableData)),
          tap(() => this.updateOpenConflicts()),
          tap(() => this.updateTables()),
          tap(() => this.focusFirstRadioButton()),
          tap(() => (this.isLoading = false))
        )
    )
  }

  private updateConflictCounts(attributes: Attribute[]): void {
    const reduce = (arr: ValidationStatus[]): Record<string, number> => {
      return arr.reduce<Record<string, number>>((acc, str) => {
        acc[str] = (acc[str] || 0) + 1
        return acc
      }, {})
    }
    this.allConflictCounts = reduce(
      attributes.flatMap((attribute) =>
        attribute.conflicts.map((conflict) => conflict.validationStatus)
      )
    )
    this.attributeConflictCounts = reduce(
      attributes
        .filter((conflict) => conflict.key !== 'referenceId')
        .flatMap((attribute) =>
          attribute.conflicts.map((conflict) => conflict.validationStatus)
        )
    )
    this.addressConflictCounts = reduce(
      this.addressConflicts.map(
        (attributeConflict) => attributeConflict.validationStatus
      )
    )
  }

  private save(): Observable<Observable<never> | SetAddressAttributesResponse> {
    this.isLoading = true

    // auto resolve NEW_ADDRESS and MOVEMENT_DATA_CONFLICT in "referenceId"
    for (const data of this.tableData.filter(
      (data) => data.key === 'referenceId'
    )) {
      const conflict = data.conflicts.find((conflict) =>
        this.autoResolveConflicts.includes(conflict.validationStatus)
      )
      if (conflict) data.target = conflict.conflictId
    }

    const data = this.tableData
      .filter((data) => data.target !== undefined)
      .map((data) => {
        return {
          key: data.key,
          ...(data.target === null ? {} : { conflictId: data.target }),
        }
      })

    if (data.length === 0)
      return throwError(() => new Error('Etwas ist schiefgelaufen!'))

    return this.attributeRepository.setAddressAttributes(this.id, data).pipe(
      switchMap((reponse) => {
        for (const responseElement of reponse.results
          .filter((value) => value.errorMessage !== null)
          .map((value) => value.errorMessage)) {
          console.error('responseElement', responseElement)
        }

        if (reponse.results.some((value) => value.errorMessage !== null)) {
          return throwError(() => new Error('Etwas ist schiefgelaufen!'))
        }
        return [reponse]
      }),
      tap(() => (this.isLoading = false))
    )
  }

  public onSaveAndNext(): void {
    this.save().subscribe({
      next: () => {
        this.notificationService.success(
          this.translateService.instant(
            'pages.conflictDetail.notification.success'
          ),
          '',
          { nzKey: 'conflictDetail.notification.success' }
        )
        if (this.hasNextConflict) {
          this.next()
        } else {
          this.back()
        }
      },
      error: () => {
        this.notificationService.error(
          this.translateService.instant(
            'pages.conflictDetail.notification.failure'
          ),
          '',
          { nzKey: 'conflictDetail.notification.failure' }
        )
        this.updateTableData()
      },
    })
  }

  public next(): void {
    this.router.navigate(['../', this.nextId], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        offset: this.offset + (this.parentShowOnlyConflicts ? 0 : 1),
      },
    })
  }

  public back(): void {
    this.router.navigate(['../../'], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
    })
  }

  public onChangeConflict(): void {
    this.updateOpenConflicts()
  }

  private updateOpenConflicts(): void {
    this.openAttributeConflicts = this.tableData
      .filter((tableData) => tableData.key !== 'referenceId')
      .filter(
        (tableData) =>
          tableData.conflicts.length !== 0 && tableData.target === undefined
      ).length

    this.openAddressConflicts = this.addressConflicts.filter(
      (value) => value.isResolution === null
    ).length

    this.unresolvedAttributeConflicts = this.tableData
      .flatMap((tableData) => tableData.conflicts)
      .filter((conflict) => conflict.isResolution === null).length
  }

  public onShowOnlyConflicts(): void {
    this.updateTables()
  }

  private updateTables(): void {
    const filteredRows = this.tableData.filter(
      (data) =>
        !(
          data.conflicts.length === 0 &&
          this.showOnlyConflicts &&
          !this.showPreviewOnly
        ) || data.key === 'oewId'
    )

    this.masterData = filteredRows.filter(
      (data) =>
        data.attributeType === 'Stammdaten' ||
        (data.attributeType === null &&
          data.conflicts.length > 0 &&
          data.key !== 'referenceId')
    )
    this.dynamicData = filteredRows.filter(
      (data) => data.attributeType === 'Bewegungsdaten'
    )
  }

  public keyValuePipeOrderCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    a: KeyValue<string, TableDataCol | undefined>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    b: KeyValue<string, TableDataCol | undefined>
  ): number {
    return 0
  }

  @HostListener('window:keydown.s', ['$event'])
  public handleSaveShortcut(event: KeyboardEvent): void {
    if (
      this.viewMode === 'clearing' &&
      !this.isLoading &&
      this.openAttributeConflicts === 0
    ) {
      event.preventDefault()
      this.onSaveAndNext()
    }
  }

  private focusFirstRadioButton(): void {
    setTimeout(() => {
      const firstRadio = this.elementRef.nativeElement.querySelector(
        'input[type="radio"]:not([tabindex="-1"])'
      ) as HTMLInputElement | null
      if (firstRadio) {
        firstRadio.focus()
      }
    }, 10)
  }
}
